/**
 * `<SquadApp>` — the player's **SQUAD** page (`decisions/0205` points 1, 3, 4): the lance's builds,
 * read-only, on a wide page. The GM has no SQUAD; the GM reads pilots through ROSTER, which edits.
 *
 * - **Who is on it (F4).** Only pilots owned by a player slot ({@link lancerPilots}, the same set
 *   CODEX's LANCERS section lists), by callsign. An unowned pilot is an NPC the GM built, and one
 *   on the GM's slot is the GM's; neither shows, and a `/squad/<id>` deep link to one shows the
 *   board, the same as a dangling id.
 * - **The tabs (F3).** A SQUAD tab, then a tab per lancer, so switching teammates is one click.
 *   SQUAD shows the board: a card per lancer with the active mech's name, four stats, its weapons
 *   and the pilot's talents.
 * - **A teammate open.** Two columns: the pilot's {@link PilotBuildSheet} on the left, the active
 *   mech's {@link MechBuildSheet} on the right. A pilot with more than one mech gets a switcher
 *   over the mech column; the choice is local state, not a URL param.
 *
 * **Read-only by construction.** Nothing here has a `dispatch`, and both sheets get
 * `null` for every capability (`edit`, notes writer, roll buttons, wear). The viewed pilot lives
 * only in the `/squad/<pilotId>` path, never `session.selectedPilotId`: viewing a teammate must not
 * retarget the pilot the viewer drives into combat.
 */
import { useState } from "react";
import { resolveActiveMechId, type MechData, type PilotData } from "@lancer-vtt/domain";
import { listManufacturers, type Compendium } from "@lancer-vtt/compendium";
import type { ComputedPilot } from "@lancer-vtt/rules";
import type { UseRoster } from "../../lib/roster/useRemoteRoster";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import type { Ref } from "../compendium/compendiumData";
import { MechBuildSheet } from "./MechBuildSheet";
import { PilotBuildSheet } from "./PilotBuildSheet";
import { RosterAvatar } from "./RosterAvatar";
import { lancerPilots } from "./rolodex/lancers";
import "./squad.css";

const MFR_NAME = new Map(listManufacturers().map((m) => [m.id, m.name]));
const ROMAN = ["", "I", "II", "III"];

/**
 * Every `PilotData` field, classified for the SHARED (other-player) sheet view — a
 * compile-time disclosure pin. `Record<keyof PilotData, …>` makes adding a field to
 * `PilotData` a **COMPILE ERROR here** until it is classified, which is the entire
 * point: a new field is never disclosed to another player by default, and the decision
 * can never be taken silently by omission. **Breaking this (a red `bun run typecheck`)
 * means: someone added a field to `PilotData` — before merging, decide whether it
 * belongs on another player's read-only view of your pilot, add it to this table either
 * way, and if `"shown"`, wire it into {@link SquadApp}/{@link PilotBuildSheet}'s
 * render.**
 *
 * Today's classification mirrors what `PilotBuildSheet` (the component this
 * window renders for a viewed pilot) actually reads, plus `owner` (surfaced by
 * `SquadApp` itself, via `slotName`, to label whose sheet this is). The one deliberate
 * `"hidden"`: `printedMechs` (per-mech current combat wear — HP/heat/repair-cap damage
 * since last Full Repair) is **not** read anywhere in this render path —
 * {@link MechBuildSheet} shows a mech's BUILD, not its current battle state,
 * deliberately. Flipping this classification is a disclosure decision, not a cleanup item.
 */
export const SHARED_SHEET_DISCLOSURE: Record<keyof PilotData, "shown" | "hidden"> = {
  id: "shown",
  callsign: "shown",
  name: "shown",
  background: "shown",
  backgroundRef: "shown",
  image: "shown",
  notes: "shown",
  // The Bondmate is set in the pilot editor; the shared sheet doesn't read it.
  bondmate: "hidden",
  level: "shown",
  mechSkills: "shown",
  skills: "shown",
  talents: "shown",
  licenses: "shown",
  coreBonuses: "shown",
  gear: "shown",
  // The Signatures' picked damage type: the shared sheet's weapon rows show it, as the owner's do.
  gearDamageTypes: "shown",
  mechs: "shown",
  printedMechs: "hidden",
  // Carried on-foot HP, current battle state like `printedMechs`; the shared sheet doesn't read it.
  currentHp: "hidden",
  activeMechId: "shown",
  owner: "shown",
};

/**
 * Every `MechData` field, classified for the SHARED (other-player) sheet view — the mech-side
 * sibling of {@link SHARED_SHEET_DISCLOSURE}. `mechs: "shown"`
 * above blesses the whole `MechData[]` array wholesale, which means adding a field to
 * `MechData` triggers **no compile error there** — this table is the second, mech-shaped wall
 * that closes that gap. `Record<keyof MechData, …>` makes adding a field to `MechData` a
 * **COMPILE ERROR here** until it is classified, same mechanism and same stakes as the pilot
 * table: a new field is never disclosed to another player by default. **Breaking this (a red
 * `bun run typecheck`) means: someone added a field to `MechData` — before merging, decide
 * whether it belongs on another player's read-only view of a shared mech, add it to this table
 * either way, and if `"shown"`, wire it into {@link MechBuildSheet}'s render.**
 *
 * Today's classification mirrors what `MechBuildSheet` (the component this window renders
 * for a viewed mech) actually reads: every field is structural build data it already displays.
 */
export const SHARED_MECH_DISCLOSURE: Record<keyof MechData, "shown" | "hidden"> = {
  id: "shown",
  name: "shown",
  frameId: "shown",
  loadouts: "shown",
  activeLoadoutIndex: "shown",
  image: "shown",
  notes: "shown",
  // Engineer 2's Revisions (ll1-features 8.6): build data, already rendered through the
  // prototype weapon's row, since `freezeMechWeapons` applies it (foreman, merge of people-apps 3).
  prototype: "shown",
};

export function SquadApp({
  roster,
  playerSlotIds,
  viewedPilotId,
  slotName,
  onSelect,
  onOpenInCompendium,
}: {
  roster: UseRoster;
  /** The player slots' ids: a pilot owned by one is a lancer (F4). */
  playerSlotIds: ReadonlySet<string>;
  /** The `/squad/<pilotId>` path's pilot, if any — never `session.selectedPilotId`. */
  viewedPilotId: string | undefined;
  /** Owner slot id → display name. */
  slotName: (slotId: string | null) => string;
  /** Open a teammate, or the board with `undefined`. */
  onSelect: (pilotId: string | undefined) => void;
  onOpenInCompendium: (ref: Ref) => void;
}) {
  const { compendium } = useCompendiumContext();
  // The mech column's pick, keyed by pilot so switching teammates never carries one over.
  const [pickedMech, setPickedMech] = useState<{ pilotId: string; mechId: string } | null>(null);

  const lancers = lancerPilots(roster.state?.pilots ?? {}, playerSlotIds);
  const viewed = lancers.find((p) => p.id === viewedPilotId);

  return (
    <div className="squad">
      <div className="squad-tabs" role="tablist" aria-label="Squad">
        <button type="button" role="tab" aria-selected={viewed === undefined} className="squad-tab squad-tab--all" onClick={() => onSelect(undefined)}>
          SQUAD · {lancers.length} PILOTS
        </button>
        {lancers.map((pilot) => (
          <button
            key={pilot.id}
            type="button"
            role="tab"
            aria-selected={pilot.id === viewed?.id}
            className="squad-tab"
            data-pilot={pilot.id}
            onClick={() => onSelect(pilot.id)}
          >
            <RosterAvatar pilot={pilot} />
            <span className="squad-tab-text">
              <span className="squad-tab-callsign">{pilot.callsign || "UNNAMED"}</span>
              <span className="squad-tab-sub">
                {slotName(pilot.owner).toUpperCase()} · LL{pilot.level}
              </span>
            </span>
          </button>
        ))}
      </div>

      {viewed === undefined ? (
        lancers.length === 0 ? (
          <div className="squad-empty">No pilots on file yet. Once your lance builds theirs, they'll show up here.</div>
        ) : (
          <div className="squad-board">
            {lancers.map((pilot) => (
              <SquadCard key={pilot.id} pilot={pilot} computed={roster.computed[pilot.id]} player={slotName(pilot.owner)} compendium={compendium} onOpen={() => onSelect(pilot.id)} />
            ))}
          </div>
        )
      ) : (
        <div className="squad-sheet">
          <section className="squad-col squad-col--pilot" aria-label="Pilot">
            <PilotBuildSheet
              pilot={viewed}
              computed={roster.computed[viewed.id]}
              playerName={slotName(viewed.owner)}
              onOpenInCompendium={onOpenInCompendium}
              // LOAD-BEARING: no notes writer and no roll buttons on another player's sheet. DICE is
              // per-client, so a roll here would seed THIS viewer's dice with someone else's stat.
              onCommitNotes={null}
              onRoll={null}
              // LOAD-BEARING: `SHARED_SHEET_DISCLOSURE.currentHp` is "hidden".
              showCarried={false}
              // LOAD-BEARING: no EDIT switch, no edit control (`decisions/0206` point 4).
              edit={null}
            />
          </section>
          <section className="squad-col squad-col--mech" aria-label="Mech">
            <MechColumn
              pilot={viewed}
              computed={roster.computed[viewed.id]}
              playerName={slotName(viewed.owner)}
              pickedMechId={pickedMech?.pilotId === viewed.id ? pickedMech.mechId : undefined}
              onPick={(mechId) => setPickedMech({ pilotId: viewed.id, mechId })}
              onOpenInCompendium={onOpenInCompendium}
            />
          </section>
        </div>
      )}
    </div>
  );
}

/** The right column: the picked mech, else the active one, with a switcher when there are several. */
function MechColumn({
  pilot,
  computed,
  playerName,
  pickedMechId,
  onPick,
  onOpenInCompendium,
}: {
  pilot: PilotData;
  computed: ComputedPilot | undefined;
  playerName: string;
  pickedMechId: string | undefined;
  onPick: (mechId: string) => void;
  onOpenInCompendium: (ref: Ref) => void;
}) {
  const activeId = resolveActiveMechId(pilot);
  const mech = pilot.mechs.find((m) => m.id === pickedMechId) ?? pilot.mechs.find((m) => m.id === activeId);
  if (mech === undefined) return <div className="squad-empty">NO MECH ON FILE</div>;
  return (
    <>
      {pilot.mechs.length > 1 && (
        <div className="squad-mechs" role="group" aria-label="Mechs">
          {pilot.mechs.map((m) => (
            <button key={m.id} type="button" className={`squad-mech-chip${m.id === activeId ? " is-active" : ""}`} aria-pressed={m.id === mech.id} data-mech={m.id} onClick={() => onPick(m.id)}>
              {m.name || "Unnamed mech"}
            </button>
          ))}
        </div>
      )}
      <MechBuildSheet
        pilot={pilot}
        mech={mech}
        computed={computed}
        playerName={playerName}
        onOpenInCompendium={onOpenInCompendium}
        onCommitNotes={null}
        onRoll={null}
        // LOAD-BEARING: `SHARED_SHEET_DISCLOSURE.printedMechs` is "hidden".
        wear={null}
        // Read-only host: no dispatch, so no EDIT (decisions/0206 point 4).
        edit={null}
      />
    </>
  );
}

/** A board card: who, the active mech, four of its stats, its weapons, the pilot's talents. */
function SquadCard({
  pilot,
  computed,
  player,
  compendium,
  onOpen,
}: {
  pilot: PilotData;
  computed: ComputedPilot | undefined;
  player: string;
  compendium: Compendium;
  onOpen: () => void;
}) {
  const mech = pilot.mechs.find((m) => m.id === resolveActiveMechId(pilot));
  const stats = computed?.mechs.find((m) => m.id === mech?.id)?.stats;
  const frame = mech?.frameId ? compendium.getById("frame", mech.frameId) : undefined;
  const mfr = frame?.source ? (MFR_NAME.get(frame.source) ?? frame.source) : undefined;
  const talents = Object.entries(pilot.talents)
    .filter(([, rank]) => rank > 0)
    .map(([id, rank]) => `${compendium.getById("talent", id)?.name ?? id} ${ROMAN[rank] ?? rank}`);
  return (
    <button type="button" className="squad-card" data-pilot={pilot.id} onClick={onOpen}>
      <span className="squad-card-who">
        <RosterAvatar pilot={pilot} />
        <span className="squad-card-names">
          <span className="squad-card-callsign">{pilot.callsign || "UNNAMED"}</span>
          {pilot.name && <span className="squad-card-name">{pilot.name}</span>}
          <span className="squad-card-sub">
            {player.toUpperCase()} · LL{pilot.level}
          </span>
        </span>
      </span>
      <span className="squad-card-mech">
        {mech === undefined ? (
          <span className="squad-card-mechname">NO MECH ON FILE</span>
        ) : (
          <>
            <span className="squad-card-mechline">
              <span className="squad-card-mechname">{mech.name || "Unnamed mech"}</span>
              <span className="squad-card-frame">{frame ? `${frame.name}${mfr ? ` // ${mfr}` : ""}` : "NO CHASSIS"}</span>
            </span>
            {stats && (
              <span className="squad-card-stats">
                {(
                  [
                    ["HP", stats.hp],
                    ["EVA", stats.evasion],
                    ["E-DEF", stats.edef],
                    ["SPD", stats.speed],
                  ] as const
                ).map(([label, value]) => (
                  <span key={label} className="squad-card-stat">
                    <span className="squad-card-stat-label">{label}</span>
                    <span className="squad-card-stat-value">{value}</span>
                  </span>
                ))}
              </span>
            )}
            <span className="squad-card-weapons">{weaponNames(mech, compendium).join(" · ") || "No weapons mounted"}</span>
          </>
        )}
      </span>
      {talents.length > 0 && <span className="squad-card-talents">{talents.join(" · ")}</span>}
    </button>
  );
}

/** The mech's mounted weapon names, from its active loadout, in mount order. */
function weaponNames(mech: MechData, compendium: Compendium): string[] {
  const loadout = mech.loadouts[mech.activeLoadoutIndex] ?? mech.loadouts[0];
  return (loadout?.mounts ?? []).flatMap((mount) =>
    mount.slots.flatMap((slot) => (slot.weaponId ? [compendium.getById("weapon", slot.weaponId)?.name ?? slot.weaponId] : [])),
  );
}
